// in src/layout/Sidebar.tsx
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { DoorOpen, MessageCircleQuestion, Settings } from "lucide-react";
import { TaxiDispatch } from "./menus/TaxiDispatch";
import { TourAgent } from "./menus/TourAgent";
import auttomlogo from "../assets/images/icon-auttom.png";
import { useLogout, usePermissions } from "react-admin";
import { TourAgentCommissionPartner } from "./menus/TourAgentCommissionPartner";
import { RentalCompanyMenuConfig } from "./menus/RentalCompanyMenuConfig";
import { TaxiDispatchCommsionPartner } from "./menus/TaxiDispatchCommsionPartner";
import { AutoService } from "./menus/AutoService";
import { NavLink, useLocation } from "react-router-dom";
import { TaxiDispatchOperator } from "./menus/TaxiDispatchOperator";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useSipContext } from "@/context/SipContext";
import LogisticMenu from "./menus/LogisticMenu";
import { disconnectTaxiSocket } from "@/hooks/useTaxiSocket";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const sipContext = useSipContext();
  const permissions = usePermissions();
  const logout = useLogout();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { toggleSidebar } = useSidebar();
  
  // Show toggle button only on pages that don't have AppBar (like dispatch page)
  const showToggleInSidebar = location.pathname === "/" || location.pathname === "/dispatch";

  const handleLogoClick = (e: React.MouseEvent) => {
    if (showToggleInSidebar) {
      e.preventDefault();
      toggleSidebar();
    }
  };

  return (
    <Sidebar 
      collapsible="icon" 
      className={cn(
        "transition-all duration-200 ease-in-out",
        isMobile && "z-50"
      )}
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {showToggleInSidebar ? (
              <SidebarMenuButton
                onClick={handleLogoClick}
                className="data-[slot=sidebar-menu-button]:!p-1.5 mt-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                title="Toggle sidebar"
              >
                <img
                  src={auttomlogo}
                  width={28}
                  height={28}
                  alt="auttom logo"
                  className="inset-0"
                />
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton
                asChild
                className="data-[slot=sidebar-menu-button]:!p-1.5 mt-2"
              >
                <NavLink to="/">
                  <img
                    src={auttomlogo}
                    width={28}
                    height={28}
                    alt="auttom logo"
                    className="inset-0"
                  />
                </NavLink>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {permissions && permissions.data && (
          <>
            {permissions.data.settings_type == "taxi" &&
              permissions.data.user_role == "owner" && <TaxiDispatch />}
            {permissions.data.settings_type == "taxi" &&
              permissions.data.user_role == "operator" && (
                <TaxiDispatchOperator />
              )}
            {permissions.data.settings_type == "taxi" &&
              permissions.data.user_role == "commission_partner" && (
                <TaxiDispatchCommsionPartner />
              )}
            {permissions.data.settings_type == "rental" &&
              permissions.data.user_role == "owner" && (
                <RentalCompanyMenuConfig />
              )}
            {permissions.data.settings_type == "rental" &&
              permissions.data.user_role == "commission_partner" && (
                <RentalCompanyMenuConfig />
              )}
            {permissions.data.settings_type == "transportation_agency" &&
              permissions.data.user_role == "owner" && <TourAgent />}
            {permissions.data.settings_type == "transportation_agency" &&
              permissions.data.user_role == "commission_partner" && (
                <TourAgentCommissionPartner />
              )}
            {permissions.data.settings_type == "auto_service" &&
              permissions.data.user_role == "owner" && <AutoService />}
              {permissions.data.settings_type == "logistics" &&
              permissions.data.user_role == "owner" && <LogisticMenu />}
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <div className="p-1">
          <SidebarMenu className="mb-4">
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink to="/settings">
                  <Settings />
                  <span>Settings</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink to="/support">
                  <MessageCircleQuestion />
                  <span>Support</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <a onClick={() => {
                  // Disconnect SIP before logout
                  console.log('Disconnecting SIP before logout');
                  sipContext.disconnect();

                  // Disconnect websocket before logout
                  console.log('Disconnecting taxi websocket before logout');
                  disconnectTaxiSocket();

                  // Small delay to ensure disconnections complete
                  setTimeout(() => {
                    logout();
                  }, 200);
                }} className="cursor-pointer">
                  <DoorOpen />
                  <span>Logout</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <a href="">
                                    <MessageSquareText />
                                    <span>Feedback</span>
                                </a>
                            </SidebarMenuButton>
                        </SidebarMenuItem> */}
          </SidebarMenu>
          {/* <Card className="shadow-none">
                        <form>
                            <CardHeader className="p-4 pb-0">
                                <CardTitle className="text-sm">Subscribe to our newsletter</CardTitle>
                                <CardDescription>
                                    Opt-in to receive updates and news about the sidebar.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="grid gap-2.5 p-4">
                                <SidebarInput type="email" placeholder="Email" />
                                <Button
                                    className="w-full bg-sidebar-primary text-sidebar-primary-foreground shadow-none"
                                    size="sm"
                                >
                                    Subscribe
                                </Button>
                            </CardContent>
                        </form>
                    </Card> */}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
